/**
 * ThreeScene — thin orchestrator that composes the 3D viewer.
 *
 * All heavy logic has been extracted into focused modules:
 *   scene/       — Canvas, cameras, controls, background, lighting, helpers, bounds
 *   model/       — ModelRenderer, WireframeOverlay, geometry lifecycle
 *   materials/   — material creation, in-place updates, disposal
 *   postprocessing/ — EffectComposer with stable bloom + SSAO
 *   toolbar/     — overlay UI toggles
 *
 * This file wires them together and owns the local viewer state.
 *
 * Update ordering guarantee:
 *   1. React state update (context or local)
 *   2. useMaterialManager runs (useLayoutEffect) — materials updated in-place
 *      synchronously before paint
 *   3. React reconciliation — R3F scene graph updates with new props
 *   4. R3F render loop — renders scene with up-to-date materials
 *   5. PostProcessing — always-mounted EffectComposer applies effects to the
 *      already-correct scene (no remount, no stale frame)
 */

import {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
} from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useColor } from '@/contexts/ColorContext';
import {
  useSceneSettings,
  type SceneSettings,
} from '@/contexts/SceneSettingsContext';

import {
  MeasurementSystem,
  type MeasurementSystemHandle,
} from '@/components/viewer/MeasurementSystem';
import {
  ObjectInspectorSensor,
  InspectorTooltip,
  type TooltipState,
} from '@/components/viewer/ObjectInspector';
import type { SceneLight, PiPCamera, CameraPathWaypoint, SvgOverlay, CloneDef } from '@/worker/types';
import type { InteractiveConfig } from '@shared/types';

// Sub-modules
import {
  SceneCanvas,
  type CameraAnimationTarget,
} from '@/components/viewer/scene/SceneCanvas';
import { SceneBackground } from '@/components/viewer/scene/SceneBackground';
import { SceneLighting } from '@/components/viewer/scene/SceneLighting';
import { SceneHelpers } from '@/components/viewer/scene/SceneHelpers';
import { SceneBounds } from '@/components/viewer/scene/SceneBounds';
import { LightWireframes } from '@/components/viewer/scene/LightWireframes';
import { CameraWireframes } from '@/components/viewer/scene/CameraWireframes';
import {
  PiPOverlays,
  PIP_W,
  PIP_H,
  PIP_GAP,
  PIP_COL_STEP,
  PIP_ROW_STEP,
} from '@/components/viewer/pip/PiPOverlays';
import { SvgOverlays } from '@/components/viewer/svg/SvgOverlays';
import { ModelRenderer } from '@/components/viewer/model/ModelRenderer';
import { WireframeOverlay } from '@/components/viewer/model/WireframeOverlay';
import { PolygonOverlay } from '@/components/viewer/model/PolygonOverlay';
import { PostProcessing } from '@/components/viewer/postprocessing/PostProcessing';
import { PerformanceMonitor } from '@/components/viewer/PerformanceMonitor';
import {
  ViewerToolbar,
  DEFAULT_VIEWER_STATE,
  type ViewerState,
} from '@/components/viewer/toolbar/ViewerToolbar';
import { useMaterialManager } from '@/components/viewer/materials/useMaterialManager';
import { useTextureScripts } from '@/components/viewer/materials/useTextureScripts';
import { useShaderScripts } from '@/components/viewer/materials/useShaderScripts';
import {
  MultiViewLayout,
  type MultiViewHandle,
  type MultiViewCameraState,
} from '@/components/viewer/MultiViewLayout';
import { AnnotationSensor } from '@/components/viewer/annotations/AnnotationSensor';
import { AnnotationOverlay } from '@/components/viewer/annotations/AnnotationOverlay';
import type { AnnotationRenderData } from '@/components/viewer/annotations/annotationLayout';
import type { ProjectFile } from '@/components/files/FileManager';
import type { Parameter } from '@shared/types';

// Re-export shared type so existing consumers don't break
export type { ColoredGeometry } from '@/components/viewer/types';

import type { ColoredGeometry } from '@/components/viewer/types';
import { Camera, PictureInPicture2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toColorKey } from '@/components/viewer/ColorVisibilityToggles';

const EMPTY_COLORED_GEOMETRIES: ColoredGeometry[] = [];
const EMPTY_LIGHTS: SceneLight[] = [];
const EMPTY_PIP_CAMERAS: PiPCamera[] = [];
const EMPTY_CAMERA_PATH: CameraPathWaypoint[] = [];
const EMPTY_SVG_OVERLAYS: SvgOverlay[] = [];
const EMPTY_CLONES: CloneDef[] = [];
const EMPTY_PARAMETERS: Parameter[] = [];

// ---------------------------------------------------------------------------
// CameraSyncer — lives inside the main Canvas, pushes camera state each frame
// ---------------------------------------------------------------------------

function CameraSyncer({
  onSync,
}: {
  onSync: (state: MultiViewCameraState) => void;
}) {
  // Access the OrbitControls instance via the R3F store's `controls` object
  useFrame(({ camera, controls }) => {
    const target =
      controls && 'target' in controls
        ? (controls as { target: THREE.Vector3 }).target
        : new THREE.Vector3();

    onSync({
      position: camera.position,
      target,
      quaternion: camera.quaternion,
      zoom: (camera as THREE.OrthographicCamera).zoom ?? 1,
    });
  });

  return null;
}

// ---------------------------------------------------------------------------
// CameraCapture — always mounted inside the main Canvas; writes live camera
// state into a ref every frame so the context menu can read it without
// causing React re-renders.
// ---------------------------------------------------------------------------

function CameraCapture({
  stateRef,
}: {
  stateRef: React.MutableRefObject<MultiViewCameraState | null>;
}) {
  useFrame(({ camera, controls }) => {
    const target =
      controls && 'target' in controls
        ? (controls as { target: THREE.Vector3 }).target
        : new THREE.Vector3();

    stateRef.current = {
      position: camera.position.clone(),
      target: target.clone(),
      quaternion: camera.quaternion.clone(),
      zoom: (camera as THREE.OrthographicCamera).zoom ?? 1,
    };
  });
  return null;
}

// ---------------------------------------------------------------------------
// ThreeScene
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// TextureScriptManager — lives inside the main Canvas, manages JS textures
// ---------------------------------------------------------------------------

function TextureScriptManager({
  coloredGeometries,
  projectFiles,
  textureMapRef,
  onTextureRegistered,
  parameters,
  onHasAnimatedScripts,
  onPublishReady,
  onSubscribeReady,
  getExtraWorkers,
}: {
  coloredGeometries: import('@/components/viewer/types').ColoredGeometry[];
  projectFiles: ProjectFile[];
  textureMapRef: React.MutableRefObject<
    Map<string, Map<string, THREE.Texture>>
  >;
  onTextureRegistered: () => void;
  parameters: Parameter[];
  onHasAnimatedScripts: (hasAnimated: boolean) => void;
  /** Called once with the publish function so ThreeScene can trigger pub/sub from interactive clicks */
  onPublishReady: (publish: (key: string, value: unknown) => void) => void;
  /** Called once with the subscribe function so external callers can receive pub/sub messages */
  onSubscribeReady: (
    subscribe: (key: string, callback: (value: unknown) => void) => () => void,
  ) => void;
  /** Optional: returns extra workers (e.g. shader workers) that also receive pub/sub messages */
  getExtraWorkers?: () => Worker[];
}) {
  // useTextureScripts manages workers and writes textures into textureMapRef.
  // onTextureRegistered is called once per new texture so ModelRenderer
  // re-renders and picks up the updated ref.
  // onHasAnimatedScripts reports whether any running workers are animated
  // (fps !== 0) so the parent can switch the R3F frameloop accordingly.
  const { publish, subscribe } = useTextureScripts(
    coloredGeometries,
    projectFiles,
    textureMapRef,
    onTextureRegistered,
    parameters,
    onHasAnimatedScripts,
    getExtraWorkers,
  );

  // Surface the publish and subscribe functions to the parent via stable ref callbacks.
  // useLayoutEffect so they are available before the first paint.
  useLayoutEffect(() => {
    onPublishReady(publish);
  }, [onPublishReady, publish]);

  useLayoutEffect(() => {
    onSubscribeReady(subscribe);
  }, [onSubscribeReady, subscribe]);

  return null;
}

// ---------------------------------------------------------------------------
// ShaderScriptManager — lives inside the main Canvas, manages GLSL shader scripts
// ---------------------------------------------------------------------------

function ShaderScriptManager({
  coloredGeometries,
  projectFiles,
  shaderMaterialMapRef,
  onShaderMaterialReady,
  parameters,
  onHasTickingShaders,
  onWorkersReady,
}: {
  coloredGeometries: import('@/components/viewer/types').ColoredGeometry[];
  projectFiles: ProjectFile[];
  shaderMaterialMapRef: React.MutableRefObject<
    Map<string, THREE.ShaderMaterial>
  >;
  onShaderMaterialReady: () => void;
  parameters: Parameter[];
  onHasTickingShaders: (hasTicking: boolean) => void;
  /** Called once with a function that returns active shader worker instances.
   *  Used by TextureScriptManager to fan out pub/sub to shader workers. */
  onWorkersReady: (getWorkers: () => Worker[]) => void;
}) {
  const { getWorkers } = useShaderScripts(
    coloredGeometries,
    projectFiles,
    onShaderMaterialReady,
    parameters,
    onHasTickingShaders,
    shaderMaterialMapRef,
  );

  // Surface the worker getter to the parent via a stable ref callback.
  useLayoutEffect(() => {
    onWorkersReady(getWorkers);
  }, [onWorkersReady, getWorkers]);

  return null;
}

export function ThreeScene({
  object,
  geometry,
  coloredGeometries = EMPTY_COLORED_GEOMETRIES,
  lights = EMPTY_LIGHTS,
  pipCameras = EMPTY_PIP_CAMERAS,
  cameraPath = EMPTY_CAMERA_PATH,
  svgOverlays = EMPTY_SVG_OVERLAYS,
  clones = EMPTY_CLONES,
  isCompiling = false,
  onFitToBoundsReady,
  autoFit = true,
  isInitialFit = true,
  onFitComplete,
  productMode = false,
  showcaseMode = false,
  cameraOffsetX = 0,
  presentationMode: externalPresentationMode,
  projectFiles = [],
  parameters = EMPTY_PARAMETERS,
  viewerState: controlledViewerState,
  onViewerStateChange: onControlledViewerStateChange,
  onInsertCode,
  onReplaceCode,
  onInteraction,
  hiddenColorKeys,
  onSubscribeReady,
  onPubSubPublish,
}: {
  object?: THREE.Object3D;
  geometry?: THREE.BufferGeometry;
  coloredGeometries?: import('@/components/viewer/types').ColoredGeometry[];
  lights?: SceneLight[];
  /** PiP camera overlays defined via camera_pip() in OpenSCAD code */
  pipCameras?: PiPCamera[];
  /** Ordered intro camera path defined via camera_path() in OpenSCAD code */
  cameraPath?: CameraPathWaypoint[];
  /** SVG file overlays defined via svg_overlay() in OpenSCAD code */
  svgOverlays?: SvgOverlay[];
  /** Clone placements declared via clone() in OpenSCAD code */
  clones?: CloneDef[];
  isCompiling?: boolean;
  onFitToBoundsReady?: (fitFn: () => void) => void;
  /** Whether auto-fit is currently enabled (initial fit or user toggle) */
  autoFit?: boolean;
  /** Whether the next auto-fit should reset camera direction (first fit only) */
  isInitialFit?: boolean;
  /** Called after an auto-fit completes */
  onFitComplete?: () => void;
  /** When true, enables presentation mode by default and shows minimal toolbar */
  productMode?: boolean;
  /** When true, hides all toolbar/chrome; full-quality auto-rotating viewer */
  showcaseMode?: boolean;
  /** Horizontal pixel offset to shift the visual center (for overlay panels) */
  cameraOffsetX?: number;
  /**
   * Externally controlled presentation mode (auto-rotation).
   * When provided, overrides / syncs the internal viewerState.presentationMode.
   */
  presentationMode?: boolean;
  /** Project files — needed to resolve JS texture scripts by filename */
  projectFiles?: ProjectFile[];
  /** OpenSCAD parameters — forwarded to JS texture scripts as `params` */
  parameters?: Parameter[];
  /**
   * Optional controlled viewer state — when provided, ThreeScene uses this
   * value instead of its internal state. Allows parents (e.g. ScadViewer)
   * to drive viewer toggles from the command palette.
   */
  viewerState?: ViewerState;
  /**
   * Setter that mirrors the internal onViewerStateChange signature.
   * Required when viewerState is provided.
   */
  onViewerStateChange?: (updater: (prev: ViewerState) => ViewerState) => void;
  /**
   * Called when the user triggers an action (e.g. from the context menu) that
   * wants to append generated code to the editor.
   */
  onInsertCode?: (code: string) => void;
  /**
   * Called when a PiP panel is drag-dropped to a new slot.
   * Replaces the matched text in the editor source (undoable).
   */
  onReplaceCode?: (search: string | RegExp, replacement: string) => void;
  /**
   * Called when the user clicks a geometry region marked with interactive()
   * in the SCAD source. The caller toggles or sets the named variable and
   * triggers a recompile.
   */
  onInteraction?: (config: InteractiveConfig) => void;
  /**
   * Set of color keys (from toColorKey()) that should be hidden in the preview.
   * Geometries whose color key is in this set are excluded from rendering
   * without requiring a recompile.
   */
  hiddenColorKeys?: Set<string>;
  /**
   * Called once the pub/sub subscribe function is available.
   * Allows external callers (e.g. ParameterSection) to register subscriptions
   * for @pubsub parameter keys.
   */
  onSubscribeReady?: (
    subscribe: (key: string, callback: (value: unknown) => void) => () => void,
  ) => void;
  /**
   * Called by the ParameterSection when the user changes a @pubsub parameter
   * in the UI — publishes the new value into the pub/sub channel so that texture
   * and shader scripts receive it.
   * Fulfilled by wiring to the ThreeScene's internal publishRef.
   */
  onPubSubPublish?: (publish: (key: string, value: unknown) => void) => void;
}) {
  const { color } = useColor();
  const {
    settings: sceneSettings,
    mergedSettings,
    sceneOverrides,
    performanceMode,
    updateSSAO,
    updateBloom,
    updateShadow,
  } = useSceneSettings();

  // Performance Mode: override rendering settings.
  // mergedSettings already incorporates any scene_* code overrides on top of
  // the UI settings; performance/showcase/product modes layer on top of that.
  const effectiveSettings = useMemo<SceneSettings>(() => {
    if (performanceMode) {
      return {
        ...mergedSettings,
        shadow: { ...mergedSettings.shadow, enabled: false },
        bloom: { ...mergedSettings.bloom, enabled: false },
        ssao: { ...mergedSettings.ssao, enabled: false },
      };
    }
    // Showcase mode: high quality, all effects on
    if (showcaseMode) {
      return {
        ...mergedSettings,
        shadow: { ...mergedSettings.shadow, enabled: true, soft: true },
        bloom: { ...mergedSettings.bloom, enabled: true },
        ssao: { ...mergedSettings.ssao, enabled: true },
      };
    }
    // Product mode: keep effects enabled but use lighter settings
    if (productMode) {
      return {
        ...mergedSettings,
        shadow: { ...mergedSettings.shadow, mapSize: 1024 },
        ssao: { ...mergedSettings.ssao, samples: 16 },
      };
    }
    return mergedSettings;
  }, [mergedSettings, performanceMode, productMode, showcaseMode]);

  // Local viewer state (toolbar toggles, camera mode, etc.)
  // When controlledViewerState is provided, this internal state is unused.
  const [internalViewerState, setInternalViewerState] = useState<ViewerState>(
    () => ({
      ...DEFAULT_VIEWER_STATE,
      ...(productMode || showcaseMode
        ? { presentationMode: true, showGrid: false, showAxis: false }
        : {}),
    }),
  );

  const isControlled = controlledViewerState !== undefined;
  const viewerState = isControlled
    ? controlledViewerState
    : internalViewerState;
  const setViewerState = isControlled
    ? (onControlledViewerStateChange ?? setInternalViewerState)
    : setInternalViewerState;

  // Sync presentation mode defaults when productMode changes (uncontrolled only)
  useEffect(() => {
    if (!isControlled && (productMode || showcaseMode)) {
      setInternalViewerState((prev) => ({
        ...prev,
        presentationMode: true,
        showGrid: false,
        showAxis: false,
      }));
    }
  }, [productMode, showcaseMode, isControlled]);

  // Sync external presentation mode override (e.g. from ScadBlock toolbar)
  useEffect(() => {
    if (externalPresentationMode !== undefined) {
      setViewerState((prev) => ({
        ...prev,
        presentationMode: externalPresentationMode,
      }));
    }
  }, [externalPresentationMode]);

  // Mutual exclusivity: PiP overlays and multi-view cannot be active at the
  // same time (each PiP canvas adds a WebGL context; multi-view already uses 4).
  // When PiP cameras appear/disappear, keep the current showPiP preference but
  // disable multi-view if PiP is currently shown.
  useEffect(() => {
    if (pipCameras.length > 0 && viewerState.showPiP && viewerState.multiView) {
      setViewerState((prev) => ({ ...prev, multiView: false }));
    }
  }, [pipCameras.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grayscale mode: disable color-dependent post-processing effects.
  // Must be computed after viewerState is available.
  const grayscaleMode = viewerState.grayscaleMode;
  const finalEffectiveSettings = useMemo<SceneSettings>(() => {
    if (!grayscaleMode) return effectiveSettings;
    return {
      ...effectiveSettings,
      bloom: { ...effectiveSettings.bloom, enabled: false },
      ssao: { ...effectiveSettings.ssao, enabled: false },
    };
  }, [effectiveSettings, grayscaleMode]);

  // Background color: code override wins over the UI color picker.
  const effectiveBackgroundColor =
    sceneOverrides.backgroundColor ?? viewerState.backgroundColor;

  // Grid visibility logic:
  // - When code defines grids, the default UI grid is suppressed.
  // - The grid toggle controls ALL grids (default + code-defined).
  // - In productMode/showcaseMode the toggle starts as false, but code-defined
  //   grids are always shown since the author explicitly put them there.
  const codeGrids = sceneOverrides.grids;
  const hasCodeGrids = codeGrids !== undefined && codeGrids.length > 0;
  const showDefaultGrid = viewerState.showGrid && !hasCodeGrids;
  const activeCodeGrids = hasCodeGrids
    ? // In product/showcase mode, always show code grids (author intent).
      // In normal mode, respect the grid toggle.
      productMode || showcaseMode || viewerState.showGrid
      ? codeGrids
      : undefined
    : undefined;

  // Refs
  const modelGroupRef = useRef<THREE.Group>(null);
  const measurementRef = useRef<MeasurementSystemHandle>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);

  // Measurement drawing state (kept separate because it's set by the
  // MeasurementSystem callback and consumed by SceneCanvas for controls)
  const [isMeasuring, setIsMeasuring] = useState(false);

  // Inspector tooltip state — set by ObjectInspectorSensor (inside Canvas)
  // and consumed by InspectorTooltip (outside Canvas, plain HTML overlay)
  const [inspectorTooltip, setInspectorTooltip] = useState<TooltipState | null>(
    null,
  );
  const handleInspectorTooltipChange = useCallback(
    (tooltip: TooltipState | null) => setInspectorTooltip(tooltip),
    [],
  );

  // Stable callback for measurement count changes — avoids re-creating a new
  // function on every render which would trigger the MeasurementSystem effect
  // and cause an extra R3F canvas re-render (which disrupts Monaco's paint).
  const handleMeasurementCountChange = useCallback((count: number) => {
    setViewerState((prev) => ({ ...prev, measurementCount: count }));
  }, []);

  // Filter geometries based on exclude, ghost, and per-color visibility toggles
  const filteredGeometries = useMemo(() => {
    return coloredGeometries.filter(
      (cg) =>
        (viewerState.showExcluded || !cg.excludedFromExport) &&
        (viewerState.showGhosts || !cg.isGhost) &&
        !hiddenColorKeys?.has(toColorKey(cg.color)),
    );
  }, [coloredGeometries, viewerState.showExcluded, viewerState.showGhosts, hiddenColorKeys]);

  // Annotated geometries — subset of filtered geometries that have annotation metadata
  const annotatedGeometries = useMemo(
    () => filteredGeometries.filter((cg) => cg.annotation != null),
    [filteredGeometries],
  );

  // Stable callback for annotation data changes
  const handleAnnotationDataChange = useCallback(
    (data: AnnotationRenderData[]) => setAnnotationRenderData(data),
    [],
  );

  // Viewport size via ResizeObserver on the container element.
  // Used by AnnotationOverlay to compute layout in pixel space.
  useEffect(() => {
    const container = viewerContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setViewportSize({
          width: Math.round(width),
          height: Math.round(height),
        });
      }
    });
    observer.observe(container);
    // Initial size
    const rect = container.getBoundingClientRect();
    setViewportSize({
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    });
    return () => observer.disconnect();
  }, []);

  // --- Material lifecycle for imported objects (Phase A + B) ---
  // Runs synchronously before paint via useLayoutEffect.
  // Phase A: replace non-standard materials on object change.
  // Phase B: update existing materials in-place on settings change.
  useMaterialManager(object, finalEffectiveSettings.material, grayscaleMode);

  // Stable ref to the live texture map. Written by TextureScriptManager (inside
  // the R3F canvas), read by ModelRenderer during R3F render frames.
  // Never replaced — only mutated — so no React re-renders are triggered.
  // Outer key: geometry UUID. Inner key: channel name ("color", "roughness", etc.)
  const textureMapRef = useRef<Map<string, Map<string, THREE.Texture>>>(
    new Map(),
  );
  // Version counter: incremented by TextureScriptManager when a new texture
  // is registered, so ModelRenderer re-renders and picks it up from the ref.
  const [textureVersion, setTextureVersion] = useState(0);
  const bumpTextureVersion = useCallback(
    () => setTextureVersion((v) => v + 1),
    [],
  );

  // Stable ref to the live shader material map.
  // Written by ShaderScriptManager (inside Canvas), read by ModelRenderer.
  const shaderMaterialMapRef = useRef<Map<string, THREE.ShaderMaterial>>(
    new Map(),
  );
  // Version counter: incremented when a new shader material is ready.
  const [shaderVersion, setShaderVersion] = useState(0);
  const bumpShaderVersion = useCallback(
    () => setShaderVersion((v) => v + 1),
    [],
  );

  // Stable ref to the publish function surfaced by useTextureScripts via
  // TextureScriptManager.  Populated once on mount via onPublishReady.
  // Used by handleInteractionWithPublish to push values from interactive()
  // and interactive_publish() clicks directly into the pub/sub channel.
  const publishRef = useRef<((key: string, value: unknown) => void) | null>(
    null,
  );
  const handlePublishReady = useCallback(
    (fn: (key: string, value: unknown) => void) => {
      publishRef.current = fn;
      // Notify external callers (e.g. ParameterSection) that the publish
      // function is ready so they can push @pubsub UI changes into the channel.
      onPubSubPublish?.(fn);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Stable ref to the subscribe function surfaced by useTextureScripts via
  // TextureScriptManager. Populated once on mount via onSubscribeReady.
  // Forwarded to the onSubscribeReady prop so external callers (e.g. the
  // ParameterSection @pubsub integration) can register subscriptions.
  const subscribeRef = useRef<
    | ((key: string, callback: (value: unknown) => void) => () => void)
    | null
  >(null);
  const handleSubscribeReady = useCallback(
    (
      fn: (key: string, callback: (value: unknown) => void) => () => void,
    ) => {
      subscribeRef.current = fn;
      // Notify external listeners who want the subscribe function
      onSubscribeReady?.(fn);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Per-key cycle index for interactive_publish() cycle mode.
  // Tracks which option in the `options` array will be published next.
  // Keyed by publishKey; reset to 0 when the geometries recompile (the ref
  // is stable across re-renders so it never triggers a re-render itself).
  const publishCycleIndexRef = useRef<Map<string, number>>(new Map());

  // Keep a stable ref to the latest parameters so handleInteractionWithPublish
  // can read current values without being a dep of useCallback.
  const parametersRef = useRef(parameters);
  parametersRef.current = parameters;

  /**
   * Intercepts interactive() / interactive_publish() clicks before forwarding
   * to the upstream onInteraction handler.
   *
   * For interactive() with a publishKey: computes the new value (same
   * toggle/cycle/set logic as ScadViewer) and publishes it, then forwards
   * the config to onInteraction for the normal parameter write + recompile.
   *
   * For interactive_publish(): publishes only — skips the upstream call so
   * no parameter write or recompile occurs.
   */
  const handleInteractionWithPublish = useCallback(
    (config: InteractiveConfig) => {
      if (config.publishKey !== undefined) {
        // Compute the value that will be published.
        let publishValue: unknown;

        if (config.options !== undefined && config.options.length > 0) {
          // Cycle mode — advance the per-key index in publishCycleIndexRef.
          // For interactive_publish() there is no SCAD parameter to read the
          // current index from, so we track it client-side.
          const opts = config.options as Array<string | number>;
          const prev = publishCycleIndexRef.current.get(config.publishKey) ?? 0;
          const next = (prev + 1) % opts.length;
          publishCycleIndexRef.current.set(config.publishKey, next);
          publishValue = opts[next];
        } else if (config.value !== undefined) {
          // Explicit-value mode — always the same value.
          publishValue = config.value;
        } else if (!config.publishOnly) {
          // Toggle mode for interactive() — read current param value to flip it.
          const param = parametersRef.current.find(
            (p) => p.name === config.variable,
          );
          const current = param?.value;
          publishValue =
            current === true || current === 'true' ? false : true;
        } else {
          // interactive_publish() with no value/options — publish a signal (true).
          publishValue = true;
        }

        publishRef.current?.(config.publishKey, publishValue);
      }

      // For publish-only regions, skip the upstream parameter write.
      if (!config.publishOnly) {
        onInteraction?.(config);
      }
    },
    [onInteraction],
  );

  // Whether any running texture-script worker is animated (fps !== 0).
  // Reported by useTextureScripts via the onHasAnimatedScripts callback.
  // Only animated scripts require continuous ("always") rendering — static
  // scripts (fps=0) and throttled scripts below 1 fps work fine in demand mode
  // since they call invalidate() when their single frame arrives.
  const [hasAnimatedTextureScripts, setHasAnimatedTextureScripts] =
    useState(false);

  // Whether any running shader script has a tick() function — those require
  // continuous ("always") rendering for the rAF-driven uniform update loop.
  const [hasTickingShaderScripts, setHasTickingShaderScripts] =
    useState(false);

  // Whether any custom light has an orbit defined — those lights animate via
  // useFrame and call invalidate() themselves, but we also need frameloop="always"
  // for the wireframe orbit visualization which runs its own useFrame.
  const hasOrbitingLights = useMemo(
    () => lights.some((l) => 'orbit' in l && l.orbit),
    [lights],
  );

  // Whether any custom light has LFO bindings — those lights animate via
  // useFrame each frame to modulate intensity/color and need frameloop="always".
  const hasLFOLights = useMemo(
    () => lights.some((l) => l.lfo !== undefined),
    [lights],
  );

  // Whether any geometry has a motion() wrapper — those meshes animate via
  // useFrame each frame and need frameloop="always".
  const hasMotionGeometries = useMemo(
    () => filteredGeometries.some((cg) => cg.motion != null),
    [filteredGeometries],
  );

  // Annotation overlay data — computed by AnnotationSensor inside the Canvas,
  // consumed by AnnotationOverlay outside the Canvas.
  const [annotationRenderData, setAnnotationRenderData] = useState<
    AnnotationRenderData[]
  >([]);

  // True while the camera is being moved — overlay hides until movement stops.
  const [cameraMoving, setCameraMoving] = useState(false);
  const handleCameraMovingChange = useCallback(
    (moving: boolean) => setCameraMoving(moving),
    [],
  );

  // Viewport dimensions for annotation layout — measured from the container element.
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const onHasAnimatedScripts = useCallback(
    (hasAnimated: boolean) => setHasAnimatedTextureScripts(hasAnimated),
    [],
  );

  const onHasTickingShaders = useCallback(
    (hasTicking: boolean) => setHasTickingShaderScripts(hasTicking),
    [],
  );

  // Ref holding the shader worker getter function, populated by ShaderScriptManager
  // via onWorkersReady. Used by TextureScriptManager to fan out pub/sub to shader workers.
  const shaderWorkersGetterRef = useRef<(() => Worker[]) | null>(null);
  const handleShaderWorkersReady = useCallback((getWorkers: () => Worker[]) => {
    shaderWorkersGetterRef.current = getWorkers;
  }, []);
  const getShaderWorkers = useCallback((): Worker[] => {
    return shaderWorkersGetterRef.current?.() ?? [];
  }, []);

  // Ref to the MultiViewLayout handle (used to sync wireframe camera each frame)
  const multiViewRef = useRef<MultiViewHandle>(null);

  // Ref that always holds the latest camera state — written every frame by
  // CameraCapture inside the main Canvas; read on context menu actions.
  const liveCameraStateRef = useRef<MultiViewCameraState | null>(null);

  // Animation target set when the user clicks a PiP panel, or when
  // scene_camera() is detected in the source.
  const [cameraAnimationTarget, setCameraAnimationTarget] =
    useState<CameraAnimationTarget | null>(null);

  // Apply scene_camera() override: set camera type + jump to position/target/fov.
  // Fires once per distinct camera override (keyed on serialized values).
  // Uses duration=0 so the camera snaps instantly without an animated transition.
  const sceneCameraOverride = sceneOverrides.camera;
  const sceneCameraKey = sceneCameraOverride
    ? JSON.stringify(sceneCameraOverride)
    : null;
  useEffect(() => {
    if (!sceneCameraOverride) return;

    // Apply camera type (perspective ↔ orthographic)
    if (sceneCameraOverride.type !== undefined) {
      setViewerState((prev) => ({
        ...prev,
        isOrthographic: sceneCameraOverride.type === 'orthographic',
      }));
    }

    // Snap camera to position/target/fov if any positional override is set
    if (
      sceneCameraOverride.position !== undefined ||
      sceneCameraOverride.target !== undefined ||
      sceneCameraOverride.fov !== undefined
    ) {
      setCameraAnimationTarget({
        position: sceneCameraOverride.position ?? [-100, 100, 100],
        target: sceneCameraOverride.target ?? [0, 0, 0],
        fov: sceneCameraOverride.fov ?? 45,
        transitions: ['direct'],
        easing: 'exponential',
        duration: 0,
        orbitTurns: 1,
        orbitAxis: 'auto',
        zoomOutFactor: 2.5,
        rollAngle: 360,
        arcHeight: 30,
        bounceAmplitude: 0.3,
        bounceFrequency: 2,
        shakeIntensity: 2,
        shakeFrequency: 8,
        shakePhase: 'end',
        tiltAngle: 30,
        fovPulseAmount: 20,
        fovPulseDirection: 'out',
        randomness: 0,
      });
    }
  }, [sceneCameraKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Animate the main camera to the position/target of the clicked PiP panel. */
  const handlePiPCameraClick = useCallback((cam: PiPCamera) => {
    setCameraAnimationTarget({
      position: cam.position,
      target: cam.target,
      fov: cam.fov,
      transitions: cam.transitions,
      easing: cam.easing,
      duration: cam.duration,
      orbitTurns: cam.orbitTurns,
      orbitAxis: cam.orbitAxis,
      zoomOutFactor: cam.zoomOutFactor,
      rollAngle: cam.rollAngle,
      arcHeight: cam.arcHeight,
      bounceAmplitude: cam.bounceAmplitude,
      bounceFrequency: cam.bounceFrequency,
      shakeIntensity: cam.shakeIntensity,
      shakeFrequency: cam.shakeFrequency,
      shakePhase: cam.shakePhase,
      tiltAngle: cam.tiltAngle,
      fovPulseAmount: cam.fovPulseAmount,
      fovPulseDirection: cam.fovPulseDirection,
      randomness: cam.randomness,
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Camera path playback
  //
  // Design:
  //  - Plays automatically once after the first compile (isCompiling true→false).
  //  - Plays in source order (cameraPath[0], [1], ..., [N-1]).
  //  - The last waypoint becomes the final camera resting position.
  //  - When any waypoints are defined, autoFit is suppressed so the path
  //    controls the initial camera position.
  //  - Any user interaction (pointer down) skips to the last waypoint instantly.
  //  - A replay callback is exposed to allow the toolbar "Replay" button.
  // ---------------------------------------------------------------------------

  /** Whether the intro path is currently playing (including the first instant snap). */
  const [cameraPathPlaying, setCameraPathPlaying] = useState(false);

  /** Index of the waypoint currently being animated toward. */
  const cameraPathIndexRef = useRef(0);

  /**
   * Whether the first compile has already happened for this ThreeScene mount.
   * The path plays only once on the very first compile — not on recompiles.
   */
  const firstCompileDoneRef = useRef(false);

  /** Stable ref to the latest cameraPath so callbacks don't go stale. */
  const cameraPathRef = useRef(cameraPath);
  cameraPathRef.current = cameraPath;

  /** Stable ref to the playing state so event handlers can read it without stale closure. */
  const cameraPathPlayingRef = useRef(cameraPathPlaying);
  cameraPathPlayingRef.current = cameraPathPlaying;

  /**
   * Convert a CameraPathWaypoint to a CameraAnimationTarget (same shape, different type name).
   * This keeps the types decoupled — waypoints don't know about SceneCanvas internals.
   */
  const waypointToAnimationTarget = useCallback(
    (wp: CameraPathWaypoint, duration?: number): CameraAnimationTarget => ({
      position: wp.position,
      target: wp.target,
      fov: wp.fov,
      transitions: wp.transitions,
      easing: wp.easing,
      duration: duration !== undefined ? duration : wp.duration,
      orbitTurns: wp.orbitTurns,
      orbitAxis: wp.orbitAxis,
      zoomOutFactor: wp.zoomOutFactor,
      rollAngle: wp.rollAngle,
      arcHeight: wp.arcHeight,
      bounceAmplitude: wp.bounceAmplitude,
      bounceFrequency: wp.bounceFrequency,
      shakeIntensity: wp.shakeIntensity,
      shakeFrequency: wp.shakeFrequency,
      shakePhase: wp.shakePhase,
      tiltAngle: wp.tiltAngle,
      fovPulseAmount: wp.fovPulseAmount,
      fovPulseDirection: wp.fovPulseDirection,
      randomness: wp.randomness,
    }),
    [],
  );

  /**
   * Start the camera path from the beginning.
   * Snaps instantly to the first waypoint's position (duration=0), then plays
   * the first waypoint's transition. Called on first compile and by the replay button.
   */
  const startCameraPath = useCallback(() => {
    const path = cameraPathRef.current;
    if (path.length === 0) return;

    cameraPathIndexRef.current = 0;
    setCameraPathPlaying(true);

    if (path.length === 1) {
      // Single waypoint: just animate to it directly (no instant snap needed)
      setCameraAnimationTarget(waypointToAnimationTarget(path[0]));
    } else {
      // Multiple waypoints: snap to the first, then immediately start its animation.
      // We set the target with duration=0 to snap the camera, then set a tiny
      // timeout to let React flush before setting the real animated target.
      setCameraAnimationTarget(waypointToAnimationTarget(path[0], 0));
      // After the snap resolves (next frame), start the real animation from [0]
      // The onAnimationComplete handler will advance to [1], [2], ...
    }
  }, [waypointToAnimationTarget]);

  /** Skip to the last waypoint instantly — called on any user interaction during playback. */
  const skipCameraPath = useCallback(() => {
    const path = cameraPathRef.current;
    if (!cameraPathPlayingRef.current || path.length === 0) return;
    setCameraPathPlaying(false);
    cameraPathIndexRef.current = path.length - 1;
    setCameraAnimationTarget(waypointToAnimationTarget(path[path.length - 1], 0));
  }, [waypointToAnimationTarget]);

  /**
   * Called by CameraAnimator (via SceneCanvas) when each animation completes.
   * Advances to the next waypoint or ends the path.
   */
  const handlePathAnimationComplete = useCallback(() => {
    if (!cameraPathPlayingRef.current) return;
    const path = cameraPathRef.current;
    const nextIdx = cameraPathIndexRef.current + 1;
    if (nextIdx >= path.length) {
      // All waypoints done — path finished
      setCameraPathPlaying(false);
      return;
    }
    cameraPathIndexRef.current = nextIdx;
    setCameraAnimationTarget(waypointToAnimationTarget(path[nextIdx]));
  }, [waypointToAnimationTarget]);

  /**
   * Detect first compile completion (isCompiling: true → false) and start path.
   * Uses a ref to ensure it only fires once, not on subsequent recompiles.
   */
  const wasCompilingRef = useRef(false);
  useEffect(() => {
    const justFinished = wasCompilingRef.current && !isCompiling;
    wasCompilingRef.current = isCompiling;

    if (justFinished && !firstCompileDoneRef.current) {
      firstCompileDoneRef.current = true;
      // Only auto-play on first compile if path is defined
      if (cameraPathRef.current.length > 0) {
        startCameraPath();
      }
    }
  }, [isCompiling, startCameraPath]);

  /**
   * Whether the effective autoFit should be suppressed because a camera path
   * is active. When a path is defined, it controls the initial camera position
   * and fit-to-zoom would fight with it.
   */
  const effectiveAutoFit = autoFit && cameraPath.length === 0;

  // Viewer context menu state: null = hidden, {x,y} = shown at viewport coords
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const ctxMenuRef = useRef<HTMLDivElement>(null);

  // Stable callback passed into CameraSyncer inside the main Canvas
  const handleCameraSync = useCallback((state: MultiViewCameraState) => {
    multiViewRef.current?.syncCamera(state);
  }, []);

  // Ref for the viewer container so we can attach a native contextmenu listener.
  // We use a native listener (not React's onContextMenu) because the WebGL
  // <canvas> calls preventDefault() on the event before it reaches React's
  // synthetic event system, so the React handler would never fire.
  // (viewerContainerRef is declared in the Refs section above.)

  useEffect(() => {
    const container = viewerContainerRef.current;
    if (!container || !onInsertCode) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      setCtxMenu({ x: e.clientX, y: e.clientY });
    };

    container.addEventListener('contextmenu', handleContextMenu);
    return () =>
      container.removeEventListener('contextmenu', handleContextMenu);
  }, [onInsertCode]);

  // Dismiss the context menu when clicking outside it
  useEffect(() => {
    if (!ctxMenu) return;
    const dismiss = (e: MouseEvent) => {
      if (
        ctxMenuRef.current &&
        !ctxMenuRef.current.contains(e.target as Node)
      ) {
        setCtxMenu(null);
      }
    };
    window.addEventListener('mousedown', dismiss, true);
    return () => window.removeEventListener('mousedown', dismiss, true);
  }, [ctxMenu]);

  // Helper: round to 2 decimal places for cleaner generated code
  const r = (n: number) => Math.round(n * 100) / 100;

  /** Create a camera_pip() snippet from the current live camera state and
   *  append it to the end of the editor code. */
  const handleCreatePiPCamera = useCallback(() => {
    setCtxMenu(null);
    if (!onInsertCode) return;
    const cam = liveCameraStateRef.current;
    if (!cam) return;

    const px = r(cam.position.x);
    const py = r(cam.position.y);
    const pz = r(cam.position.z);
    const tx = r(cam.target.x);
    const ty = r(cam.target.y);
    const tz = r(cam.target.z);

    /**
     * Find a placement that doesn't overlap any existing PiP panel.
     *
     * Strategy: walk candidate slots in reading order (left→right, top→bottom)
     * on the same grid used by the drag-and-drop slot overlay (PIP_W × PIP_H
     * cells separated by PIP_GAP). Return the first free slot. Falls back to
     * (PIP_GAP, PIP_GAP) when all slots are occupied.
     */
    const findFreeSlot = (): { x: number; y: number } => {
      if (pipCameras.length === 0) return { x: PIP_GAP, y: PIP_GAP };

      const maxCols = Math.floor((1 - PIP_GAP) / PIP_COL_STEP);
      const maxRows = Math.floor((1 - PIP_GAP) / PIP_ROW_STEP);

      const overlaps = (cx: number, cy: number): boolean => {
        return pipCameras.some((pip) => {
          // Expand the existing panel by PIP_GAP on all sides for clearance.
          const ex = pip.x - PIP_GAP;
          const ey = pip.y - PIP_GAP;
          const ew = pip.width + PIP_GAP * 2;
          const eh = pip.height + PIP_GAP * 2;
          // AABB intersection test.
          return (
            cx < ex + ew && cx + PIP_W > ex && cy < ey + eh && cy + PIP_H > ey
          );
        });
      };

      for (let row = 0; row < maxRows; row++) {
        for (let col = 0; col < maxCols; col++) {
          const cx = PIP_GAP + col * PIP_COL_STEP;
          const cy = PIP_GAP + row * PIP_ROW_STEP;
          if (!overlaps(cx, cy)) return { x: cx, y: cy };
        }
      }

      return { x: PIP_GAP, y: PIP_GAP };
    };

    const { x, y } = findFreeSlot();

    const code = `camera_pip(\n  position=[${px}, ${py}, ${pz}],\n  target=[${tx}, ${ty}, ${tz}],\n  fov=45,\n  x=${r(x)}, y=${r(y)},\n  width=${PIP_W}, height=${PIP_H}\n);`;
    onInsertCode(code);
  }, [onInsertCode, pipCameras]);

  /** Create a scene_camera() snippet from the current live camera state and
   *  append it to the end of the editor code.
   *  If a scene_camera() call already exists the user will end up with two;
   *  they can delete the old one — same pattern as other scene_* inserts. */
  const handleSetSceneCamera = useCallback(() => {
    setCtxMenu(null);
    if (!onInsertCode) return;
    const cam = liveCameraStateRef.current;
    if (!cam) return;

    const px = r(cam.position.x);
    const py = r(cam.position.y);
    const pz = r(cam.position.z);
    const tx = r(cam.target.x);
    const ty = r(cam.target.y);
    const tz = r(cam.target.z);
    const isOrtho = viewerState.isOrthographic;
    const fovLine = isOrtho ? '' : `\n  fov=45,`;
    const typeLine = isOrtho ? `\n  type="orthographic",` : '';

    const code = `scene_camera(\n  position=[${px}, ${py}, ${pz}],\n  target=[${tx}, ${ty}, ${tz}],${typeLine}${fovLine}\n);`;
    onInsertCode(code);
  }, [onInsertCode, viewerState.isOrthographic]);

  /**
   * Viewer state change handler that enforces mutual exclusivity between
   * multi-view and PiP overlays.
   * - Enabling multi-view while PiP is shown → turn off PiP
   * - Enabling PiP (showPiP=true) while multi-view is on → turn off multi-view
   */
  const handleViewerStateChange = useCallback(
    (updater: (prev: ViewerState) => ViewerState) => {
      setViewerState((prev) => {
        const next = updater(prev);
        if (
          next.multiView &&
          !prev.multiView &&
          next.showPiP &&
          pipCameras.length > 0
        ) {
          // User just turned on multi-view while PiP is showing → disable PiP
          return { ...next, showPiP: false };
        }
        if (
          next.showPiP &&
          !prev.showPiP &&
          next.multiView &&
          pipCameras.length > 0
        ) {
          // User just turned on PiP while multi-view is showing → disable multi-view
          return { ...next, multiView: false };
        }
        return next;
      });
    },
    [pipCameras.length],
  );

  const mainCanvasContent = (
    <SceneCanvas
      isOrthographic={viewerState.isOrthographic}
      presentationMode={viewerState.presentationMode}
      measurementEnabled={viewerState.measurementEnabled}
      isMeasuring={isMeasuring}
      hasPostProcessing={
        finalEffectiveSettings.ssao.enabled ||
        finalEffectiveSettings.bloom.enabled
      }
      shadows={
        finalEffectiveSettings.shadow.enabled
          ? finalEffectiveSettings.shadow.soft
            ? 'soft'
            : 'basic'
          : false
      }
      productMode={productMode}
      showcaseMode={showcaseMode}
      cameraOffsetX={cameraOffsetX}
      // Blog embeds (productMode) never use the screenshot/download feature,
      // so we can skip preserveDrawingBuffer to save GPU memory.
      preserveDrawingBuffer={!productMode}
      hasActiveTextureScripts={
        hasAnimatedTextureScripts ||
        hasTickingShaderScripts ||
        ((hasOrbitingLights || hasLFOLights) && !viewerState.lightAnimationsPaused) ||
        (hasMotionGeometries && !viewerState.lightAnimationsPaused)
      }
      cameraAnimationTarget={cameraAnimationTarget}
      onAnimationComplete={handlePathAnimationComplete}
    >
      {/* 1. Background — rendered first, sits behind everything */}
      <SceneBackground color={effectiveBackgroundColor} />

      {/* 2. Lighting */}
      <SceneLighting
        sceneSettings={finalEffectiveSettings}
        showMovingLight={viewerState.showMovingLight}
        showGrid={viewerState.showGrid}
        wireframeOpacity={viewerState.wireframeOpacity}
        lights={lights}
        skipEnvironment={productMode}
        lightAnimationsPaused={viewerState.lightAnimationsPaused}
        projectFiles={projectFiles}
      />

      {/* 3. Bounds + Model — only model meshes affect the bounding box */}
      <SceneBounds
        geometry={geometry}
        object={object}
        coloredGeometries={filteredGeometries}
        isOrthographic={viewerState.isOrthographic}
        isCompiling={isCompiling}
        autoFit={effectiveAutoFit}
        isInitialFit={isInitialFit}
        onFitToBoundsReady={onFitToBoundsReady}
        onFitComplete={onFitComplete}
      >
        <ModelRenderer
          ref={modelGroupRef}
          object={object}
          geometry={geometry}
          coloredGeometries={filteredGeometries}
          clones={clones}
          color={color}
          materialSettings={finalEffectiveSettings.material}
          wireframeOpacity={viewerState.wireframeOpacity}
          grayscaleMode={grayscaleMode}
          textureMapRef={textureMapRef}
          textureVersion={textureVersion}
          shaderMaterialMapRef={shaderMaterialMapRef}
          shaderVersion={shaderVersion}
          onInteraction={handleInteractionWithPublish}
          animationsPaused={viewerState.lightAnimationsPaused}
        />
        {/* Texture script manager — lives inside Canvas for useThree() access */}
        <TextureScriptManager
          coloredGeometries={filteredGeometries}
          projectFiles={projectFiles}
          textureMapRef={textureMapRef}
          onTextureRegistered={bumpTextureVersion}
          parameters={parameters}
          onHasAnimatedScripts={onHasAnimatedScripts}
          onPublishReady={handlePublishReady}
          onSubscribeReady={handleSubscribeReady}
          getExtraWorkers={getShaderWorkers}
        />
        {/* Shader script manager — lives inside Canvas for useThree() access */}
        <ShaderScriptManager
          coloredGeometries={filteredGeometries}
          projectFiles={projectFiles}
          shaderMaterialMapRef={shaderMaterialMapRef}
          onShaderMaterialReady={bumpShaderVersion}
          parameters={parameters}
          onHasTickingShaders={onHasTickingShaders}
          onWorkersReady={handleShaderWorkersReady}
        />
      </SceneBounds>

      {/* 4. Wireframe overlay */}
      <WireframeOverlay
        geometry={geometry}
        coloredGeometries={filteredGeometries}
        wireframeOpacity={viewerState.wireframeOpacity}
      />

      {/* 4b. Polygon mode overlay — all triangle edges on top of solid mesh */}
      <PolygonOverlay
        geometry={geometry}
        coloredGeometries={filteredGeometries}
        polygonMode={viewerState.polygonMode}
      />
      <LightWireframes
        lights={lights}
        wireframeOpacity={viewerState.wireframeOpacity}
        lightAnimationsPaused={viewerState.lightAnimationsPaused}
      />
      <CameraWireframes
        pipCameras={pipCameras}
        wireframeOpacity={viewerState.wireframeOpacity}
      />

      {/* 5. Post-processing — stable EffectComposer, no remount on toggle */}
      <PostProcessing
        sceneSettings={finalEffectiveSettings}
        multisampling={showcaseMode ? 8 : productMode ? 4 : 8}
      />

      {/* 6. Helpers — grid + axis, not inside Bounds so they don't affect fit */}
      <SceneHelpers
        showGrid={showDefaultGrid}
        showAxis={viewerState.showAxis}
        codeGrids={activeCodeGrids}
      />

      {/* 7. Measurement system */}
      <MeasurementSystem
        ref={measurementRef}
        enabled={viewerState.measurementEnabled}
        modelGroupRef={modelGroupRef}
        onDrawingChange={setIsMeasuring}
        onMeasurementCountChange={handleMeasurementCountChange}
      />

      {/* 8. Object inspector sensor — raycasting only, no HTML rendered here */}
      <ObjectInspectorSensor
        enabled={viewerState.inspectorEnabled}
        modelGroupRef={modelGroupRef}
        onTooltipChange={handleInspectorTooltipChange}
      />

      {/* 9. Annotation sensor — bounding-box projection + hover tracking */}
      {viewerState.annotationsEnabled && annotatedGeometries.length > 0 && (
        <AnnotationSensor
          annotatedGeometries={annotatedGeometries}
          enabled={viewerState.annotationsEnabled}
          modelGroupRef={modelGroupRef}
          onDataChange={handleAnnotationDataChange}
          onMovingChange={handleCameraMovingChange}
        />
      )}

      <PerformanceMonitor />

      {/* Always capture live camera state for context-menu use */}
      <CameraCapture stateRef={liveCameraStateRef} />

      {/* Camera sync for multi-view wireframe panel */}
      {viewerState.multiView && <CameraSyncer onSync={handleCameraSync} />}
    </SceneCanvas>
  );

  return (
    <div
      ref={viewerContainerRef}
      className="relative h-full w-full overflow-hidden"
      onPointerDown={cameraPathPlaying ? skipCameraPath : undefined}
    >
      {viewerState.multiView ? (
        /* Split layout: top 50% = 4-panel grid, bottom 50% = main viewer */
        <div className="flex h-full w-full flex-col">
          {/* Top half: 2×2 static panels */}
          <div className="relative flex-1 overflow-hidden border-b border-border">
            <MultiViewLayout
              ref={multiViewRef}
              geometry={geometry}
              coloredGeometries={filteredGeometries}
              backgroundColor={effectiveBackgroundColor}
              grayscaleMode={grayscaleMode}
            />
          </div>
          {/* Bottom half: main interactive viewer */}
          <div className="relative flex-1 overflow-hidden">
            {mainCanvasContent}
          </div>
        </div>
      ) : (
        /* Normal full-height layout */
        mainCanvasContent
      )}

      {/* PiP camera overlays — HTML positioned over the main canvas.
          rightExclusionPx: cameraOffsetX is half the panel width that was
          used to shift the render center, so doubling it gives the full
          occupied right-side pixel width to keep PiP panels clear of UI panels. */}
      {viewerState.showPiP &&
        !viewerState.multiView &&
        pipCameras.length > 0 && (
          <PiPOverlays
            cameras={pipCameras}
            geometry={geometry}
            coloredGeometries={filteredGeometries}
            color={color}
            backgroundColor={effectiveBackgroundColor}
            sceneSettings={finalEffectiveSettings}
            lights={lights}
            rightExclusionPx={cameraOffsetX * 2}
            onCameraClick={handlePiPCameraClick}
            onReplaceCode={onReplaceCode}
          />
        )}

      {/* SVG file overlays — HTML positioned over the main canvas */}
      <SvgOverlays overlays={svgOverlays} projectFiles={projectFiles} />

      {/* Inspector tooltip — Portal to document.body, outside Canvas */}
      <InspectorTooltip tooltip={inspectorTooltip} />

      {/* Annotation overlay — SVG lines + text labels above the 3D view */}
      {viewerState.annotationsEnabled &&
        annotatedGeometries.length > 0 &&
        !cameraMoving && (
          <AnnotationOverlay
            renderData={annotationRenderData}
            viewportWidth={viewportSize.width}
            viewportHeight={viewportSize.height}
          />
        )}

      {/* Toolbar overlay — pure HTML/CSS, not inside the R3F canvas */}
      <ViewerToolbar
        state={viewerState}
        onStateChange={handleViewerStateChange}
        ssaoEnabled={sceneSettings.ssao.enabled}
        onSSAOToggle={(v) => updateSSAO({ enabled: v })}
        bloomEnabled={sceneSettings.bloom.enabled}
        onBloomToggle={(v) => updateBloom({ enabled: v })}
        softShadowsEnabled={sceneSettings.shadow.soft}
        onSoftShadowsToggle={(v) => updateShadow({ soft: v })}
        measurementRef={measurementRef}
        minimal={productMode}
        showcaseMode={showcaseMode}
        backgroundColorOverridden={sceneOverrides.backgroundColor !== undefined}
        hasPiPCameras={pipCameras.length > 0}
        hasAnnotations={annotatedGeometries.length > 0}
        hasGhosts={coloredGeometries.some((cg) => cg.isGhost)}
        hasOrbitingLights={hasOrbitingLights}
        hasLFOLights={hasLFOLights}
        hasMotionGeometries={hasMotionGeometries}
        hasCameraPath={cameraPath.length > 0}
        onReplayCameraPath={startCameraPath}
      />

      {/* Context menu popup — rendered at fixed cursor position.
          Shown when the user right-clicks anywhere inside the viewer;
          the native contextmenu event is captured on viewerContainerRef
          because the WebGL canvas swallows it before React sees it. */}
      {onInsertCode && ctxMenu && (
        <div
          ref={ctxMenuRef}
          className="fixed z-50 min-w-[11rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
        >
          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
            Preview
          </div>
          <div className="-mx-1 my-1 h-px bg-border" />
          <button
            className={cn(
              'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:bg-accent focus:text-accent-foreground',
            )}
            onClick={handleCreatePiPCamera}
          >
            <PictureInPicture2 className="h-4 w-4 shrink-0" />
            Create PiP Camera here
          </button>
          <button
            className={cn(
              'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:bg-accent focus:text-accent-foreground',
            )}
            onClick={handleSetSceneCamera}
          >
            <Camera className="h-4 w-4 shrink-0" />
            Set as initial scene camera
          </button>
        </div>
      )}
    </div>
  );
}
